/**
 * <dialog data-cc-offer> — the add-to-cart offer ("A cup for you too"). Markup, and why
 * the deadline and the price work the way they do: sections/crewcup-atc-offer.liquid.
 *
 * FLOW
 *   1. The theme's CartLinesUpdateEvent fires BEFORE the add request is sent. If the
 *      offer can still be shown, Smart Cart is held closed right then
 *      (Rebuy.SmartCart.skip_open), so it never flashes open under the pop-up.
 *   2. Once the add succeeds: was a trigger-collection product (a 20oz) added, and is no
 *      offer product (a 40oz) in the cart already? If not — or if anything else is on
 *      screen — the hold is released and the cart opens exactly as it would have.
 *   3. Otherwise the pop-up opens, the countdown running to the deadline stored the
 *      first time this shopper saw it.
 *   4. Accept: the cup goes in by its own variant id, the code joins the cart's codes,
 *      and Shopify's answer is checked. If the cup was not discounted it comes back out.
 *   5. Every close opens the cart: the shopper has just added something.
 *
 * WHICH ADDS COUNT. Only CartLinesUpdateEvent with action 'add' and context 'product':
 * the product form, the sticky bar, both quick adds and the 2-Pack builder. The CrewCup
 * add-ons, every Rebuy add (the free-warranty gift included) and Buy it now dispatch no
 * such event, so none of them can trigger it.
 *
 * FAILS CLOSED. Without working localStorage the deadline cannot be kept, and a deadline
 * that restarts is a fake one — so no storage, no offer. Every other doubt (a failed
 * fetch, a slow page, another pop-up open) hides the offer for that add without using
 * it up.
 */
import { StandardEvents } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

if (!window.__crewcupAtcOffer) {
  window.__crewcupAtcOffer = true;

  // Smart Cart is never held longer than this while the offer is still being decided —
  // the add request itself is inside that window, which on a slow phone connection is
  // most of it. Once the pop-up is actually open it owns the hold until it closes.
  const HOLD_FALLBACK_MS = 6000;
  const ENDED_CLOSE_MS = 2200;
  const DAY_MS = 86400000;

  // Same pairing as blocks/crewcup-accessory-upsells.liquid ("MATCHING DESIGN FIRST"):
  // by handle, which is exact — titles paired Chalkboard Doodles with a Pizza cup.
  const MATCH_RULES = [
    ['stainless-steel-kids-crewcup-20oz', 'stainless-steel-crewcup-40oz'],
    ['spill-proof-kids-crewcup-20oz', 'spill-proof-adult-crewcup-40oz'],
  ];

  /* ------------------------------------------------------------ helpers */

  /** @returns {HTMLDialogElement | null} */
  const getDialog = () => {
    const el = document.querySelector('dialog[data-cc-offer]');
    return el instanceof HTMLDialogElement ? el : null;
  };

  /** @param {HTMLDialogElement} dialog */
  const readConfig = (dialog) => {
    try {
      const config = JSON.parse(dialog.querySelector('[data-cc-offer-config]')?.textContent || '{}');
      return config && config.code ? config : null;
    } catch {
      return null;
    }
  };

  const rootUrl = () => window.Shopify?.routes?.root ?? '/';

  const getJSON = async (path) => {
    const res = await fetch(`${rootUrl()}${path}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`${path} ${res.status}`);
    return res.json();
  };

  const postJSON = async (path, body) => {
    const res = await fetch(`${rootUrl()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Shopify's own wording ("…is already sold out", "You can't add more…").
      throw new Error(data.description || data.message || 'This cup could not be added. Please try again.');
    }
    return data;
  };

  const track = (event, data = {}) => {
    try {
      window.Shopify?.analytics?.publish?.('crewcup_atc_offer', { event, ...data });
    } catch {
      /* tracking must never break the offer */
    }
  };

  /* ---------------------------------------------------- storage: the deadline */

  /** localStorage if it really works (private windows and blocked cookies throw). */
  const storage = (() => {
    try {
      const store = window.localStorage;
      store.setItem('__cco_probe', '1');
      store.removeItem('__cco_probe');
      return store;
    } catch {
      return null;
    }
  })();

  const stateKey = (config) => `crewcup-atc-offer:${config.campaign}`;

  /**
   * @returns {null | {status?: string, shownAt?: number, expiresAt?: number}}
   *   null when the state cannot be read at all (fail closed), {} when never shown.
   */
  const readState = (config) => {
    if (!storage) return null;
    try {
      const raw = storage.getItem(stateKey(config));
      if (!raw) return {};
      const state = JSON.parse(raw);
      return state && typeof state === 'object' ? state : null;
    } catch {
      return null;
    }
  };

  const writeState = (config, state) => {
    if (!storage) return false;
    try {
      storage.setItem(stateKey(config), JSON.stringify(state));
      return true;
    } catch {
      return false;
    }
  };

  const cooledDown = (config, state) =>
    Number.isFinite(state.shownAt) && Date.now() - state.shownAt >= config.cooldownDays * DAY_MS;

  /** Can this shopper still be shown the offer? Resuming an unexpired window counts. */
  const offerAvailable = (config, state) => {
    if (!state) return false;
    if (!state.status) return true;
    if (cooledDown(config, state)) return true;
    return state.status === 'active' && Number.isFinite(state.expiresAt) && Date.now() < state.expiresAt;
  };

  /* ------------------------------------------------------ Smart Cart hold */

  /**
   * Holds Smart Cart closed from the add until the shopper has decided. Leaves the flag
   * alone when Rebuy is not loaded, or when something else already holds it (the CrewCup
   * add-ons do, for their quiet adds) — the rule crewcup-accessory-upsells.js follows.
   * skip_open never clears itself, so every path below releases it.
   */
  const holdCart = () => {
    const smartCart = window.Rebuy?.SmartCart;
    if (!smartCart || smartCart.skip_open) return null;
    smartCart.skip_open = true;
    const hold = { smartCart, released: false, timer: 0 };
    hold.timer = window.setTimeout(() => releaseCart(hold, true), HOLD_FALLBACK_MS);
    return hold;
  };

  /** The patched theme drawer opens Smart Cart when it is ready, the native drawer if not (snippets/rebuy-native-cart.liquid). */
  const openCart = () => {
    const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };

  const releaseCart = (hold, open) => {
    if (hold) {
      if (hold.released) return;
      hold.released = true;
      window.clearTimeout(hold.timer);
      if (window.Rebuy?.SmartCart === hold.smartCart) delete hold.smartCart.skip_open;
    }
    if (open) openCart();
  };

  /* ------------------------------------------------ nothing else on screen */

  /** offsetParent is not a visibility test; see crewcup-promo-popup.js for the bug that proved it. */
  const isReallyVisible = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    try {
      if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
      }
    } catch {
      /* fall through */
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const inFixedOverlay = (el) => {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      if (getComputedStyle(node).position === 'fixed') return true;
    }
    return false;
  };

  /** One pop-up at a time. Checked right before opening: all of this can change during the add. */
  const somethingElseIsOpen = (dialog) => {
    for (const open of document.querySelectorAll('dialog[open]')) {
      if (open !== dialog) return true;
    }
    if (document.querySelector('theme-drawer[open]')) return true;
    if (window.Rebuy?.SmartCart?.visible) return true;
    if (isReallyVisible(document.querySelector('.rebuy-widget.widget-display-popup'))) return true;
    // Klaviyo's sign-up pop-ups: a fixed overlay around form.klaviyo-form. Inline forms are fine.
    for (const form of document.querySelectorAll('form.klaviyo-form')) {
      if (isReallyVisible(form) && inFixedOverlay(form)) return true;
    }
    return false;
  };

  /* ------------------------------------------------ collection membership */

  /** @type {Map<string, Promise<Set<number>>>} */
  const idsCache = new Map();

  /** Product ids in a collection, from templates/collection.cc-offer-ids.liquid, cached for the session. */
  const collectionIds = (handle) => {
    if (!idsCache.has(handle)) {
      const load = (async () => {
        const key = `crewcup-atc-offer:ids:${handle}`;
        try {
          const cached = window.sessionStorage.getItem(key);
          if (cached) return new Set(JSON.parse(cached).map(Number));
        } catch {
          /* no session cache; fetch */
        }
        const res = await fetch(`${rootUrl()}collections/${encodeURIComponent(handle)}?view=cc-offer-ids`);
        if (!res.ok) throw new Error(`ids ${res.status}`);
        const data = JSON.parse((await res.text()).trim());
        const ids = (data.ids || []).map(Number);
        try {
          window.sessionStorage.setItem(key, JSON.stringify(ids));
        } catch {
          /* fine */
        }
        return new Set(ids);
      })();
      // A failed fetch must not be cached for the rest of the page.
      load.catch(() => idsCache.delete(handle));
      idsCache.set(handle, load);
    }
    return /** @type {Promise<Set<number>>} */ (idsCache.get(handle));
  };

  /** @type {Promise<Element[]> | null} */
  let allTilesLoad = null;

  /**
   * Every offerable design, from templates/collection.cc-offer-tiles.liquid. The page
   * itself carries only the first few (the pop-up is on every page); these are fetched
   * once per page, only after a 20oz add. If this fails the printed tiles are offered.
   */
  const allTiles = (config) => {
    if (!allTilesLoad) {
      allTilesLoad = (async () => {
        const res = await fetch(`${rootUrl()}collections/${encodeURIComponent(config.offerCollection)}?view=cc-offer-tiles`);
        if (!res.ok) throw new Error(`tiles ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        return [...doc.querySelectorAll('.cc-up__option--design')];
      })();
      allTilesLoad.catch(() => (allTilesLoad = null));
    }
    return allTilesLoad;
  };

  /* --------------------------------------------------------------- money */

  /**
   * The deal price, rounded the way Shopify rounds a percentage off one line: the
   * discount is rounded DOWN to the cent (2799 at 25% -> 699.75 -> 699 off -> 2100),
   * checked against a live cart with the MOMCUP25 code. Math.round quoted $20.99 for a
   * cup the cart then charged $21.00. An "Amount off each" code (amountOff, minor units)
   * takes off exactly that, never below zero. Quoted only in the shop's own currency with
   * no ABConvert test on the product — anywhere else the pop-up says the discount is
   * applied in the cart rather than risk quoting a number the cart will not show.
   */
  const dealPrice = (config, price) =>
    config.amountOff > 0
      ? Math.max(0, price - config.amountOff)
      : price - Math.floor((price * config.percent) / 100);

  /** The struck-through price: the compare-at price when the cup has one, as on its product page. */
  const wasPrice = (tile) => {
    const price = Number(tile.dataset.price);
    const compareAt = Number(tile.dataset.compareAt);
    return compareAt > price ? compareAt : price;
  };

  /** The deal without a number, for wherever it cannot be quoted. */
  const plainDeal = (config) => (config.amountOff > 0 ? 'Special pop-up price' : `Extra ${config.percent}% off`);

  const canQuote = (config, productId) =>
    config.currency === config.primaryCurrency &&
    typeof config.moneyFormat === 'string' &&
    !window.abconvertPriceCartTransformTestDict?.[String(productId)];

  const money = (config, minor) => formatMoney(minor, config.moneyFormat, config.currency);

  /* --------------------------------------------------------------- tiles */

  // "Highland Cow Spill Proof Adult CREWCUP 40oz" -> "Highland Cow", as the tile snippet does.
  const shortName = (title) =>
    String(title)
      .split(' Spill Proof')[0]
      .split(' Stainless')[0]
      .split(' CREWCUP')[0]
      .split(' CrewCup')[0]
      .trim();

  const matchHandle = (handle) => {
    for (const [from, to] of MATCH_RULES) {
      if (handle.includes(from)) return handle.replace(from, to);
    }
    return null;
  };

  /** @param {HTMLElement} fieldset */
  const tilesIn = (fieldset) => /** @type {HTMLElement[]} */ ([...fieldset.querySelectorAll('.cc-up__option--design')]);

  const imageUrl = (src, width) => {
    const url = String(src).startsWith('//') ? `https:${src}` : String(src);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  /** The matching print when it is not among the rendered tiles: fetched, then built from a copy of one. */
  const fetchMatchTile = async (fieldset, config, handle, offerIds) => {
    const template = tilesIn(fieldset)[0];
    if (!template) return null;
    const res = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`);
    if (!res.ok) return null;
    const product = await res.json();
    const variant = product.variants?.[0];
    if (!product.available || product.variants.length !== 1 || !variant?.available) return null;
    if (!offerIds.has(Number(product.id))) return null;

    const tile = /** @type {HTMLElement} */ (template.cloneNode(true));
    // The copied price text is the template's, formatted by Liquid in the shopper's
    // currency. It stays only when the price really is the same; renderPrices()
    // rewrites it where the deal can be quoted. Otherwise no price beats a wrong one.
    if (String(variant.price) !== template.dataset.price && !canQuote(config, product.id)) {
      tile.querySelector('.cc-up__option-price')?.replaceChildren();
    }
    tile.dataset.handle = product.handle;
    tile.dataset.productId = String(product.id);
    tile.dataset.price = String(variant.price);
    tile.dataset.compareAt = String(variant.compare_at_price ?? '');
    const input = tile.querySelector('input[data-cc-up-variant]');
    if (input instanceof HTMLInputElement) {
      input.value = String(variant.id);
      input.checked = false;
      input.dataset.title = shortName(product.title);
    }
    const name = tile.querySelector('.cc-up__option-name');
    if (name) name.textContent = shortName(product.title);
    const img = tile.querySelector('img');
    if (img && product.featured_image) {
      img.src = imageUrl(product.featured_image, 360);
      img.srcset = [120, 180, 240, 360].map((w) => `${imageUrl(product.featured_image, w)} ${w}w`).join(', ');
    }
    return tile;
  };

  /**
   * Offer only cups the code will discount, put the matching print first and pre-select
   * it. With no match nothing is pre-selected: the shopper picks. `fullSet` (every
   * design, see allTiles) replaces the printed tiles once.
   */
  const prepareTiles = async (dialog, config, addedHandle, offerIds, fullSet = null) => {
    const fieldset = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-cc-offer-options]'));
    if (!fieldset) return false;

    const group = fieldset.querySelector('input[data-cc-up-variant]')?.getAttribute('name');
    if (fullSet?.length && group && fieldset.dataset.ccOfferFull !== 'true') {
      fieldset.dataset.ccOfferFull = 'true';
      for (const tile of tilesIn(fieldset)) tile.remove();
      for (const tile of fullSet) {
        const copy = /** @type {HTMLElement} */ (document.importNode(tile, true));
        copy.querySelector('input[data-cc-up-variant]')?.setAttribute('name', group);
        fieldset.append(copy);
      }
    }

    for (const tile of tilesIn(fieldset)) {
      if (!offerIds.has(Number(tile.dataset.productId))) tile.remove();
      tile.querySelector('[data-cc-offer-tag]')?.remove();
      const input = tile.querySelector('input[data-cc-up-variant]');
      if (input instanceof HTMLInputElement) input.checked = false;
    }

    const target = addedHandle ? matchHandle(addedHandle) : null;
    let match = target ? tilesIn(fieldset).find((tile) => tile.dataset.handle === target) : null;
    if (target && !match) match = await fetchMatchTile(fieldset, config, target, offerIds).catch(() => null);

    if (match) {
      const legend = fieldset.querySelector('legend');
      if (legend) legend.after(match);
      else fieldset.prepend(match);
      const tag = document.createElement('span');
      tag.className = 'cc-up__option-tag';
      tag.dataset.ccOfferTag = '';
      tag.textContent = config.matchTag || 'Matches your cup';
      match.querySelector('.cc-up__option-name')?.before(tag);
      const input = match.querySelector('input[data-cc-up-variant]');
      if (input instanceof HTMLInputElement) input.checked = true;
    }

    return tilesIn(fieldset).length > 0;
  };

  /** Each tile's own price, rewritten as the deal price with the was price struck through. */
  const renderPrices = (dialog, config) => {
    for (const tile of dialog.querySelectorAll('.cc-up__option--design')) {
      if (!(tile instanceof HTMLElement)) continue;
      const priceEl = tile.querySelector('.cc-up__option-price');
      const price = Number(tile.dataset.price);
      if (!priceEl || !Number.isFinite(price) || !canQuote(config, tile.dataset.productId)) continue;

      const now = document.createElement('span');
      now.className = 'cc-offer__now';
      now.textContent = money(config, dealPrice(config, price));
      const was = document.createElement('s');
      was.className = 'cc-offer__was';
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = 'Regular price ';
      was.append(hidden, money(config, wasPrice(tile)));
      priceEl.replaceChildren(now, was);
    }
  };

  /** The deal line's [price]: the chosen cup's deal price, or the first cup's before one is chosen. */
  const renderDeal = (dialog, config, tile) => {
    const deal = dialog.querySelector('[data-cc-offer-deal]');
    const template = deal instanceof HTMLElement ? deal.dataset.template || '' : '';
    if (!deal || !template.includes('[price]')) return;
    const source = tile instanceof HTMLElement ? tile : dialog.querySelector('.cc-up__option--design');
    const price = Number(source instanceof HTMLElement ? source.dataset.price : NaN);
    deal.textContent =
      source instanceof HTMLElement && Number.isFinite(price) && canQuote(config, source.dataset.productId)
        ? template.replace('[price]', money(config, dealPrice(config, price)))
        : `${plainDeal(config)} — only in this pop-up`;
  };

  /** Keep the large preview and saving tied to the exact variant being added. */
  const renderFeatured = (dialog, config, tile) => {
    const featured = dialog.querySelector('[data-cc-offer-featured]');
    const label = dialog.querySelector('[data-cc-offer-choose-label]');
    const count = dialog.querySelector('[data-cc-offer-count]');
    const total = dialog.querySelectorAll('input[data-cc-up-variant]').length;
    if (count) count.textContent = `${total} ${total === 1 ? 'design' : 'designs'}`;
    if (label) label.textContent = tile ? 'Change design' : 'Choose your design';
    renderDeal(dialog, config, tile);
    if (!(featured instanceof HTMLElement)) return;
    featured.hidden = !tile;
    if (!(tile instanceof HTMLElement)) return;

    const title = tile.querySelector('input')?.dataset.title || '';
    const name = featured.querySelector('[data-cc-offer-name]');
    if (name) name.textContent = title;
    const match = featured.querySelector('[data-cc-offer-match]');
    if (match instanceof HTMLElement) match.hidden = !tile.querySelector('[data-cc-offer-tag]');

    const photo = featured.querySelector('[data-cc-offer-photo]');
    if (photo && photo.dataset.handle !== tile.dataset.handle) {
      const source = tile.querySelector('img');
      photo.replaceChildren();
      photo.dataset.handle = tile.dataset.handle || '';
      if (source) {
        const img = source.cloneNode(true);
        const url = new URL(source.src, document.baseURI);
        url.searchParams.set('width', '640');
        img.src = url.href;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.loading = 'eager';
        img.alt = `${title} Adult CrewCup, 40oz`;
        photo.append(img);
      }
    }

    const price = Number(tile.dataset.price);
    const priceEl = featured.querySelector('[data-cc-offer-price]');
    const saving = featured.querySelector('[data-cc-offer-saving]');
    if (priceEl) {
      priceEl.replaceChildren();
      if (canQuote(config, tile.dataset.productId) && Number.isFinite(price)) {
        const deal = dealPrice(config, price);
        const regular = wasPrice(tile);
        const now = document.createElement('span');
        now.className = 'cc-offer__now';
        now.textContent = money(config, deal);
        const was = document.createElement('s');
        was.className = 'cc-offer__was';
        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = 'Regular price ';
        was.append(hidden, money(config, regular));
        priceEl.append(now, was);
        if (saving) saving.textContent = `Save ${money(config, regular - deal)} (${Math.round(((regular - deal) * 100) / regular)}%)`;
      } else if (saving) {
        saving.textContent = plainDeal(config);
      }
    }
  };

  /* ------------------------------------------------------------- the pop-up */

  /**
   * @type {null | {
   *   dialog: HTMLDialogElement, config: any, hold: any, state: any, editor: boolean,
   *   accepted: boolean, ended: boolean, busy: boolean, endPending: boolean,
   *   interval: number, lastFocused: HTMLElement | null
   * }}
   */
  let showing = null;

  const els = (dialog) => ({
    clock: dialog.querySelector('[data-cc-offer-clock]'),
    timerLabel: dialog.querySelector('[data-cc-offer-timer-label]'),
    ends: dialog.querySelector('[data-cc-offer-ends]'),
    accept: /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-cc-offer-accept]')),
    acceptLabel: dialog.querySelector('[data-cc-offer-accept-label]'),
    error: /** @type {HTMLElement | null} */ (dialog.querySelector('[data-cc-offer-error]')),
  });

  const formatClock = (ms) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  };

  const updateAccept = () => {
    const s = showing;
    if (!s) return;
    const { accept, acceptLabel } = els(s.dialog);
    if (!accept || !acceptLabel) return;
    if (s.busy) return;
    if (s.ended) {
      accept.disabled = true;
      acceptLabel.textContent = 'Deal ended';
      return;
    }
    const picked = s.dialog.querySelector('input[data-cc-up-variant]:checked');
    const tile = picked instanceof HTMLInputElement ? picked.closest('.cc-up__option') : null;
    renderFeatured(s.dialog, s.config, tile);
    accept.disabled = !tile || s.editor;
    if (!(tile instanceof HTMLElement)) {
      acceptLabel.textContent = 'Pick a design';
      return;
    }
    const price = Number(tile.dataset.price);
    acceptLabel.textContent =
      canQuote(s.config, tile.dataset.productId) && Number.isFinite(price)
        ? `Add my 40oz — ${money(s.config, dealPrice(s.config, price))}`
        : s.config.amountOff > 0
          ? 'Add at the pop-up price'
          : `Add with ${s.config.percent}% off`;
  };

  const showError = (message) => {
    const error = showing && els(showing.dialog).error;
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const end = () => {
    const s = showing;
    if (!s || s.ended || s.accepted) return;
    // A tap made in time is honoured: if an add is under way, finish it first.
    if (s.busy) {
      s.endPending = true;
      return;
    }
    s.ended = true;
    window.clearInterval(s.interval);
    if (!s.editor) {
      writeState(s.config, { ...s.state, status: 'expired' });
      track('expire');
    }
    const { clock, timerLabel } = els(s.dialog);
    s.dialog.dataset.state = 'ended';
    if (timerLabel) timerLabel.textContent = 'This deal has ended';
    if (clock) clock.textContent = '';
    updateAccept();
    window.setTimeout(() => {
      if (showing === s && s.dialog.open) s.dialog.close();
    }, ENDED_CLOSE_MS);
  };

  const tick = () => {
    const s = showing;
    if (!s || s.ended) return;
    const left = s.state.expiresAt - Date.now();
    const { clock } = els(s.dialog);
    if (clock) clock.textContent = formatClock(left);
    if (left <= 0) end();
  };

  /**
   * @param {HTMLDialogElement} dialog
   * @param {any} config
   * @param {any} hold
   * @param {boolean} [editor]
   */
  const show = (dialog, config, hold, editor = false) => {
    const now = Date.now();
    let state = { status: 'active', shownAt: now, expiresAt: now + config.minutes * 60000 };

    if (!editor) {
      const stored = readState(config);
      if (!offerAvailable(config, stored)) {
        releaseCart(hold, Boolean(hold));
        return;
      }
      // Resume an unexpired window with its ORIGINAL deadline; otherwise this is the
      // first showing (or the first after the cooldown) and the deadline starts now.
      if (stored && stored.status === 'active' && !cooledDown(config, stored) && now < Number(stored.expiresAt)) {
        state = /** @type {any} */ (stored);
      } else if (!writeState(config, state)) {
        releaseCart(hold, Boolean(hold));
        return;
      }
    }

    // The pop-up now owns the hold until it closes.
    if (hold) window.clearTimeout(hold.timer);

    showing = {
      dialog,
      config,
      hold,
      state,
      editor,
      accepted: false,
      ended: false,
      busy: false,
      endPending: false,
      interval: 0,
      lastFocused: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    };

    delete dialog.dataset.state;
    const { timerLabel, ends } = els(dialog);
    if (timerLabel) timerLabel.textContent = 'Offer ends in';
    if (ends) {
      ends.textContent = `Offer ends at ${new Date(state.expiresAt).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })}.`;
    }
    showError('');
    renderPrices(dialog, config);
    const chooser = dialog.querySelector('[data-cc-offer-chooser]');
    if (chooser instanceof HTMLDetailsElement) {
      chooser.open = !dialog.querySelector('input[data-cc-up-variant]:checked');
    }
    updateAccept();
    // Only the featured image loads eagerly. Alternative designs load when revealed.

    dialog.showModal();
    lockScroll(dialog);
    tick();
    showing.interval = window.setInterval(tick, 1000);

    if (!editor) {
      const picked = dialog.querySelector('input[data-cc-up-variant]:checked');
      track('view', { matched: picked instanceof HTMLInputElement, variant_id: picked instanceof HTMLInputElement ? Number(picked.value) : null });
    }
  };

  const onClose = () => {
    const s = showing;
    if (!s) return;
    showing = null;
    window.clearInterval(s.interval);
    unlockScroll(s.dialog);

    if (!s.editor) {
      // Any close that is not a yes — the X, the backdrop, Escape, No thanks — is a no.
      if (!s.accepted && !s.ended) {
        writeState(s.config, { ...s.state, status: 'declined' });
        track('decline');
      }
      releaseCart(s.hold, false);
      openCart();
    }

    const back = s.lastFocused;
    if (back && back.isConnected && typeof back.focus === 'function') back.focus();
  };

  /** Adds the chosen cup, applies the code, and keeps the cup only if Shopify discounted it. */
  const accept = async () => {
    const s = showing;
    if (!s || s.busy || s.ended || s.editor) return;
    const picked = s.dialog.querySelector('input[data-cc-up-variant]:checked');
    if (!(picked instanceof HTMLInputElement)) return;

    const code = String(s.config.code);
    const sameCode = (other) => String(other || '').toUpperCase() === code.toUpperCase();
    const { accept: button, acceptLabel } = els(s.dialog);
    s.busy = true;
    if (button) button.disabled = true;
    button?.setAttribute('aria-busy', 'true');
    const options = s.dialog.querySelector('[data-cc-offer-options]');
    if (options instanceof HTMLFieldSetElement) options.disabled = true;
    if (acceptLabel) acceptLabel.textContent = 'Adding…';
    showError('');

    const variantId = Number(picked.value);
    const tile = picked.closest('.cc-up__option');
    const price = Number(tile instanceof HTMLElement ? tile.dataset.price : NaN);
    const quoted = canQuote(s.config, tile instanceof HTMLElement ? tile.dataset.productId : '') && Number.isFinite(price)
      ? dealPrice(s.config, price)
      : null;

    let added = false;
    /** @type {string[] | null} */
    let previousCodes = null;

    try {
      await postJSON('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
      added = true;

      // `discount` REPLACES the cart's codes, so the shopper's own codes go back in with
      // ours — the same thing assets/cart-discount.js does when a code is typed.
      const cart = await getJSON('cart.js');
      previousCodes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c && !sameCode(c));
      const updated = await postJSON('cart/update.js', { discount: [...previousCodes, code].join(',') });

      // Found by VARIANT, not by the key /cart/add.js returned: applying a discount
      // gives the line a new key, so the old one matches nothing. The offer only opens
      // when no 40oz is in the cart, so this cup's line is the only one with its variant.
      const ours = (updated.discount_codes || []).find((d) => sameCode(d.code));
      const line = (updated.items || []).find((item) => Number(item.variant_id) === variantId);
      const discounted = Boolean(line) && Number(line.line_level_total_discount ?? line.total_discount) > 0;
      // The shopper was quoted a price: they must not be charged more than it. This is
      // what catches a percent setting that no longer matches the code in admin.
      const unitPrice = line ? Number(line.final_line_price) / Math.max(1, Number(line.quantity)) : NaN;
      const asQuoted = quoted === null || unitPrice <= quoted;
      if (!ours?.applicable || !discounted || !asQuoted) {
        throw Object.assign(new Error('not-applied'), { notApplied: true });
      }

      s.accepted = true;
      writeState(s.config, { ...s.state, status: 'accepted' });
      track('accept', { variant_id: Number(picked.value) });
      const cartIcon = /** @type {any} */ (document.querySelector('cart-icon'));
      if (cartIcon && typeof cartIcon.renderCartBubble === 'function' && typeof updated.item_count === 'number') {
        cartIcon.renderCartBubble(updated.item_count);
      }
      s.dialog.close();
    } catch (error) {
      // Never leave a full-price cup in the cart that the pop-up quoted at the deal price.
      // The line is looked up again for its CURRENT key (see above), and only the one cup
      // this pop-up added is taken off it.
      if (added) {
        try {
          if (previousCodes) await postJSON('cart/update.js', { discount: previousCodes.join(',') });
          const now = await getJSON('cart.js');
          const line = (now.items || []).find((item) => Number(item.variant_id) === variantId);
          if (line) await postJSON('cart/change.js', { id: line.key, quantity: Math.max(0, Number(line.quantity) - 1) });
        } catch {
          /* best effort; the message below still tells the shopper */
        }
      }
      const notApplied = /** @type {any} */ (error)?.notApplied;
      showError(
        notApplied
          ? "Sorry, this deal couldn't be applied, so the cup wasn't added."
          : error instanceof Error
            ? error.message
            : 'This cup could not be added. Please try again.'
      );
    } finally {
      s.busy = false;
      button?.removeAttribute('aria-busy');
      if (options instanceof HTMLFieldSetElement) options.disabled = false;
      if (!s.accepted) {
        updateAccept();
        if (s.endPending) end();
      }
    }
  };

  /* --------------------------------------------------------------- trigger */

  /**
   * @param {HTMLDialogElement} dialog
   * @param {any} config
   * @param {any} hold
   * @param {Set<number>} addedIds
   * @param {Promise<any> | undefined} promise
   * @param {HTMLDialogElement | null} sourceModal
   */
  const decide = async (dialog, config, hold, addedIds, promise, sourceModal, fullSet) => {
    const result = await promise;
    if (!result || result.detail?.didError) return releaseCart(hold, Boolean(hold));

    const [cart, triggerIds, offerIds] = await Promise.all([
      getJSON('cart.js'),
      collectionIds(config.triggerCollection),
      collectionIds(config.offerCollection),
    ]);
    const items = cart.items || [];
    const trigger = items.find((item) => addedIds.has(Number(item.variant_id)) && triggerIds.has(Number(item.product_id)));
    if (!trigger || items.some((item) => offerIds.has(Number(item.product_id)))) {
      return releaseCart(hold, Boolean(hold));
    }

    // Quick add: let its own modal close (and hand focus back) first, as cart-drawer.js does.
    if (sourceModal?.open) {
      await new Promise((resolve) => sourceModal.addEventListener('close', resolve, { once: true }));
    }
    if (hold?.released) return; // the fallback already opened the cart; the offer keeps for next time

    const ready = await prepareTiles(dialog, config, trigger.handle, offerIds, await fullSet);
    if (!ready || hold?.released || showing || somethingElseIsOpen(dialog)) {
      return releaseCart(hold, Boolean(hold));
    }
    show(dialog, config, hold);
  };

  const onLinesUpdate = (/** @type {any} */ event) => {
    if (event.action !== 'add' || event.context !== 'product') return;
    const dialog = getDialog();
    if (!dialog || showing || dialog.dataset.ccOfferEditor === 'true') return;
    const config = readConfig(dialog);
    if (!config || !offerAvailable(config, readState(config))) return;

    const hold = holdCart();
    const addedIds = new Set(
      (event.lines || [])
        .map((line) => Number(String(line?.merchandiseId ?? '').match(/(\d+)$/)?.[1]))
        .filter(Number.isFinite)
    );
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    // Started now, alongside the add request; a failure just means the printed tiles.
    const fullSet = allTiles(config).catch(() => null);

    decide(dialog, config, hold, addedIds, event.promise, sourceModal, fullSet).catch(() => releaseCart(hold, Boolean(hold)));
  };

  // Capture on window: runs before any bubbling listener (the cart drawer's included),
  // so the hold is in place before anything could open the cart.
  window.addEventListener(StandardEvents.cartLinesUpdate, onLinesUpdate, true);

  /* ---------------------------------------------------------------- wiring */

  /** @param {HTMLDialogElement} dialog */
  const wire = (dialog) => {
    if (dialog.dataset.ccOfferWired === 'true') return;
    dialog.dataset.ccOfferWired = 'true';

    dialog.addEventListener('close', onClose);

    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-cc-offer-close], [data-cc-offer-decline]')) {
        dialog.close();
        return;
      }
      if (target.closest('[data-cc-offer-accept]')) {
        accept();
        return;
      }
      // Backdrop: the dialog carries no padding, so a click on the dialog itself is outside the sheet.
      if (target === dialog) dialog.close();
    });

    dialog.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-cc-up-variant]')) {
        showError('');
        updateAccept();
        if (showing && !showing.editor) track('design_change', { variant_id: Number(event.target.value) });
      }
    });

    if (dialog.dataset.ccOfferEditor === 'true') {
      // Theme editor: open while the section is selected so copy edits show live.
      // Storage and the cart are never touched here.
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target instanceof Element && event.target.contains(dialog) && !dialog.open) {
          const config = readConfig(dialog) ?? { code: 'preview', percent: 25, minutes: 10, currency: '', primaryCurrency: 'x' };
          show(dialog, config, null, true);
        }
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target instanceof Element && event.target.contains(dialog) && dialog.open) dialog.close();
      });
    }
  };

  /**
   * Fetch the two id lists while the page is idle, so the decision after an add only
   * waits for the cart. Only while the offer is still possible for this shopper: once
   * they have decided, nothing here runs again.
   */
  const prefetch = (dialog) => {
    if (dialog.dataset.ccOfferEditor === 'true') return;
    const config = readConfig(dialog);
    if (!config || !offerAvailable(config, readState(config))) return;
    const run = () => {
      collectionIds(config.triggerCollection).catch(() => {});
      collectionIds(config.offerCollection).catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 4000 });
    else window.setTimeout(run, 2000);
  };

  const init = () => {
    const dialog = getDialog();
    if (!dialog) return;
    wire(dialog);
    prefetch(dialog);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  // The clock is computed from the deadline, so a tab coming back shows the right time at once.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });

  // A scroll lock must never survive a navigation.
  window.addEventListener('pagehide', () => {
    if (showing) unlockScroll(showing.dialog);
  });

  /** For QA only: see and clear this browser's offer state. */
  window.crewcupAtcOffer = {
    state: () => {
      const dialog = getDialog();
      const config = dialog && readConfig(dialog);
      return config ? readState(config) : null;
    },
    reset: () => {
      const dialog = getDialog();
      const config = dialog && readConfig(dialog);
      if (config && storage) storage.removeItem(stateKey(config));
    },
  };
}
